/**
 * Patient Protected Route Component
 *
 * Wraps routes that require patient authentication
 * Uses SEPARATE token storage from staff (independent sessions)
 */

import { Navigate, useLocation } from 'react-router-dom'
import { isPatientAuthenticated } from '@/application/auth/auth.service'
import { APP_ROUTES } from '@/config/constants'
import { Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'

interface PatientProtectedRouteProps {
  children: React.ReactNode
}

export function PatientProtectedRoute({ children }: PatientProtectedRouteProps) {
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Check if patient is authenticated (uses separate patient token storage)
    setIsAuthenticated(isPatientAuthenticated())
    setIsChecking(false)
  }, [])

  // Show loading while checking auth state
  if (isChecking) {
    return (
      <div className="min-h-screen alto-de-pantalla flex items-center justify-center bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600 dark:text-teal-400" />
          <p className="text-gray-500 dark:text-gray-400">Verificando acceso...</p>
        </div>
      </div>
    )
  }

  // Redirect to patient login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={APP_ROUTES.PATIENT_LOGIN} state={{ from: location }} replace />
  }

  return <>{children}</>
}

/**
 * Patient Public Route Component
 *
 * Wraps routes that should only be accessible when patient is NOT authenticated
 * Uses SEPARATE token storage from staff (independent sessions)
 */
export function PatientPublicRoute({ children }: { children: React.ReactNode }) {
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Check if patient is authenticated (uses separate patient token storage)
    setIsAuthenticated(isPatientAuthenticated())
    setIsChecking(false)
  }, [])

  // Show loading while checking auth state
  if (isChecking) {
    return (
      <div className="min-h-screen alto-de-pantalla flex items-center justify-center bg-gradient-to-br from-teal-900 via-teal-800 to-cyan-900">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-teal-300" />
          <p className="text-teal-200">Cargando...</p>
        </div>
      </div>
    )
  }

  // Redirect to patient portal if already authenticated
  if (isAuthenticated) {
    return <Navigate to={APP_ROUTES.PATIENT_PORTAL.HOME} replace />
  }

  return <>{children}</>
}
